// FlowRent Admin Dashboard
import { Routes, Route, Navigate, Outlet, useParams, useNavigate } from 'react-router'
import { Capacitor } from '@capacitor/core'
import { App as CapacitorApp } from '@capacitor/app'
import { setupPushDeepLinks } from '@/lib/push/operatorPush'
import { lazy, Suspense, useEffect, type ComponentType } from 'react'
import { Spinner } from '@/components/ui/Spinner'
import { useAuth } from '@/hooks/useAuth'
import { useModuleEnabled, type ModuleKey } from '@/hooks/useModuleEnabled'
import { useSearchStore } from '@/stores'
import { LoginPage } from '@/pages/LoginPage'
import { DashboardPage } from '@/pages/DashboardPage'
import { PropertyListPage, PropertyDetailPage, PropertyFormPage } from '@/pages/properties'
import { RoomTypeDetailPage, RoomTypeFormPage } from '@/pages/room-types'
import { UnitDetailPage, UnitFormPage } from '@/pages/units'
import { CustomerListPage, CustomerDetailPage, CustomerFormPage } from '@/pages/customers'
import { SharedOwnerListPage, SharedOwnerDirectoryPage } from '@/pages/shared-owners'
import { LeaseListPage, LeaseDetailPage, LeaseFormPage } from '@/pages/leases'
import { InvoiceListPage, InvoiceDetailPage, InvoiceFormPage, InvoiceGeneratePage } from '@/pages/invoices'
import { MeterReadingsPage } from '@/pages/meters'
import { BulkReadingsPage } from '@/pages/readings'
import { TeamListPage } from '@/pages/users'
import { SettingsPage, ActivityLogPage, SubscriptionInvoiceDetailPage } from '@/pages/settings'
import { ForgotPasswordPage, ResetPasswordPage, SignupPage } from '@/pages/auth'
import { MaintenanceListPage, MaintenanceDetailPage, MaintenanceFormPage } from '@/pages/maintenance'
import { ExpenseListPage, ExpenseDetailPage, ExpenseFormPage } from '@/pages/expenses'
import { TimesheetPage } from '@/pages/timesheet/TimesheetPage'
import { ParcelListPage, ParcelDetailPage, ParcelFormPage, ScanParcelPage } from '@/pages/parcels'
import { PaymentListPage, PaymentDetailPage, PaymentFormPage, QuickPayFormPage } from '@/pages/payments'
import { ParkingFeeConfigPage } from '@/pages/parking/fees'
import { ParkingZoneListPage, ParkingZoneFormPage } from '@/pages/parking/zones'
import { VehicleListPage, VehicleFormPage } from '@/pages/parking/vehicles'
import { ParkingPermitListPage, ParkingPermitFormPage } from '@/pages/parking/permits'
import { DailyParkingLogPage } from '@/pages/parking/daily'
import { LprEventLogPage } from '@/pages/parking/lpr'
import { ParkingSessionsPage } from '@/pages/parking/sessions'
import { ParkingIndexPage } from '@/pages/parking/ParkingIndexPage'
import { CommonAreaFeeFormPage } from '@/pages/common-area-fees/CommonAreaFeeFormPage'
import { CommonAreaFeeRatesPage } from '@/pages/common-area-fees/CommonAreaFeeRatesPage'
import { VisitorWidgetSettingsPage } from '@/pages/visitor-widgets'
import { MeetingListPage, MeetingDetailPage, CommitteePage, AuditLogPage } from '@/pages/governance'
import {
  EquipmentListPage,
  EquipmentDetailPage,
  EquipmentFormPage,
  WorkOrderListPage,
  WorkOrderDetailPage,
  WorkOrderFormPage,
  VendorListPage,
  VendorDetailPage,
  VendorFormPage,
} from '@/pages/cmms'
import { BankReconciliationPage } from '@/pages/bank-reconciliation'
import { ClosingPackagePage } from '@/pages/closing-package/ClosingPackagePage'
import {
  AnnouncementListPage,
  AnnouncementFormPage,
  AnnouncementDetailPage,
} from '@/pages/announcements'
import {
  ChartOfAccountsPage,
  JournalEntryListPage,
  JournalEntryDetailPage,
  JournalEntryFormPage,
  FinancialStatementsPage,
  FixedAssetListPage,
  FixedAssetDetailPage,
  FixedAssetFormPage,
} from '@/pages/accounting'
import { DebtCaseListPage, DebtCaseDetailPage } from '@/pages/debt-collection'
import {
  CampaignsListPage,
  CampaignDetailPage,
  CatalogListPage,
  PromotionsPage,
} from '@/pages/incentives'
import { AccountDeletePage } from '@/pages/account/AccountDeletePage'
import { AccountExportPage } from '@/pages/account/AccountExportPage'
import { ProtectedLayout } from '@/components/layout'
import { OnboardingImportLayout } from '@/components/onboarding/OnboardingImportLayout'
import { useFeatureFlags } from '@/lib/featureFlags'

// ---------------------------------------------------------------------------
// WEB-H7 — route-level code splitting. The heavy route groups load on demand:
// reports (recharts), templates (@pdfme/ui Designer), sign (pdfme generator),
// onboarding (one-shot wizard), design-system (dev playground). Every lazy()
// against the same barrel resolves to ONE shared chunk for that group.
// ---------------------------------------------------------------------------
function lazyFrom<M, K extends keyof M>(loader: () => Promise<M>, name: K) {
  return lazy(() =>
    loader().then((m) => ({ default: m[name] as ComponentType })),
  )
}

const SignPage = lazyFrom(() => import('@/pages/sign'), 'SignPage')
// Public self-service parking kiosk (unauthenticated, apikey/token-scoped).
const ParkingCheckInPage = lazyFrom(() => import('@/pages/parking/kiosk'), 'CheckInPage')
const ParkingPayPage = lazyFrom(() => import('@/pages/parking/kiosk'), 'PayPage')
// EP-133 T133-020 — public visitor-booking iframe (unauthenticated, key-scoped).
const VisitorBookingPage = lazyFrom(() => import('@/pages/visitor-iframe'), 'VisitorBookingPage')
const VisitorArrivalsPage = lazyFrom(() => import('@/pages/visitor-arrivals'), 'VisitorArrivalsPage')
const OnboardingWizardPage = lazyFrom(
  () => import('@/pages/onboarding/OnboardingWizardPage'),
  'OnboardingWizardPage',
)
const ExplorerModePage = lazyFrom(
  () => import('@/pages/onboarding/ExplorerModePage'),
  'ExplorerModePage',
)
const ImportWizardPage = lazyFrom(
  () => import('@/pages/onboarding/ImportWizardPage'),
  'ImportWizardPage',
)
const ImportDetailPage = lazyFrom(
  () => import('@/pages/onboarding/ImportDetailPage'),
  'ImportDetailPage',
)
const DesignSystemPage = lazyFrom(() => import('@/pages/DesignSystemPage'), 'DesignSystemPage')

const TemplateListPage = lazyFrom(() => import('@/pages/templates'), 'TemplateListPage')
const TemplateDetailPage = lazyFrom(() => import('@/pages/templates'), 'TemplateDetailPage')
const TemplateFormPage = lazyFrom(() => import('@/pages/templates'), 'TemplateFormPage')

const reports = () => import('@/pages/reports')
const ReportsIndexPage = lazyFrom(reports, 'ReportsIndexPage')
const RevenueReportPage = lazyFrom(reports, 'RevenueReportPage')
const ParkingOccupancyReportPage = lazyFrom(reports, 'ParkingOccupancyReportPage')
const ParkingRevenueReportPage = lazyFrom(reports, 'ParkingRevenueReportPage')
const ParkingRegistryReportPage = lazyFrom(reports, 'ParkingRegistryReportPage')
const ExpenseSummaryReportPage = lazyFrom(reports, 'ExpenseSummaryReportPage')
const ExpenseTrendReportPage = lazyFrom(reports, 'ExpenseTrendReportPage')
const ProfitLossReportPage = lazyFrom(reports, 'ProfitLossReportPage')
// RPT redesign — group landings
const CollectionsGroupPage = lazyFrom(reports, 'CollectionsGroupPage')
const OccupancyGroupPage = lazyFrom(reports, 'OccupancyGroupPage')
const RecordsGroupPage = lazyFrom(reports, 'RecordsGroupPage')
const ParkingGroupPage = lazyFrom(reports, 'ParkingGroupPage')
// Collections sub-pages (RPT-5)
const CollectionStatsReportPage = lazyFrom(reports, 'CollectionStatsReportPage')
const CollectionStatsDetailsPage = lazyFrom(reports, 'CollectionStatsDetailsPage')
const TenantScorecardReportPage = lazyFrom(reports, 'TenantScorecardReportPage')
const TenantScorecardDetailPage = lazyFrom(reports, 'TenantScorecardDetailPage')
const OverdueInvoicesReportPage = lazyFrom(reports, 'OverdueInvoicesReportPage')
// Occupancy sub-pages (RPT-6)
const OccupancyStatsReportPage = lazyFrom(reports, 'OccupancyStatsReportPage')
const OccupancyStatsDetailsPage = lazyFrom(reports, 'OccupancyStatsDetailsPage')
const VacantUnitsReportPage = lazyFrom(reports, 'VacantUnitsReportPage')
// Profitability (RPT-7)
const ProfitLossGlancePage = lazyFrom(reports, 'ProfitLossGlancePage')
const ProfitLossDetailsPage = lazyFrom(reports, 'ProfitLossDetailsPage')
// Records sub-pages (RPT-8)
const TimesheetsReportPage = lazyFrom(reports, 'TimesheetsReportPage')
const MaintenanceReportPage = lazyFrom(reports, 'MaintenanceReportPage')
const CafCollectionReportPage = lazyFrom(reports, 'CafCollectionReportPage')
const BankReconciliationReportPage = lazyFrom(reports, 'BankReconciliationReportPage')
const FinancialStatementsReportPage = lazyFrom(reports, 'FinancialStatementsReportPage')

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-base-200">
        <Spinner size="xl" className="text-primary" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-base-200">
        <Spinner size="xl" className="text-primary" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (user?.role === 'staff' || user?.role === 'accountant') {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

// T129-009: Route guard that redirects the accountant off routes outside the
// money set (D-5). Used as a layout route wrapper (renders <Outlet />) around
// route groups that accountant must not reach. The server still enforces
// authorization (AC-3); this is a UX guard only.
function AccountantBlockedRoute({ children }: { children?: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-base-200">
        <Spinner size="xl" className="text-primary" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (user?.role === 'accountant') {
    return <Navigate to="/" replace />
  }

  // When used as a layout route (no children), render the nested <Outlet>.
  return children ? <>{children}</> : <Outlet />
}

// T129-009: Route guard for money-set routes accessible to admin/owner AND
// accountant, but blocked for staff. Mirrors AdminRoute but passes 'accountant'.
function MoneyRoute({ children }: { children?: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-base-200">
        <Spinner size="xl" className="text-primary" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (user?.role === 'staff') {
    return <Navigate to="/" replace />
  }

  return children ? <>{children}</> : <Outlet />
}

// D9 (EP-147): route-level guard for module-gated route groups. A typed URL
// to a disabled module used to reach the page and render a generic "Something
// went wrong" from the 403 body; this redirects to the dashboard instead, the
// way the role guards above already do. The predicate is the SAME one the
// sidebar uses for that module (useModuleEnabled), so the nav item and the
// route guard cannot drift apart. Server gates are untouched, so this is UX
// only, the 403 remains the enforcement. Layout-route usage only (renders
// <Outlet />), every current caller wraps a route group, not a single route.
function ModuleRoute({ module }: { module: ModuleKey }) {
  const { isAuthenticated, isLoading } = useAuth()
  const moduleEnabled = useModuleEnabled(module)

  if (isLoading || moduleEnabled === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-base-200">
        <Spinner size="xl" className="text-primary" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (!moduleEnabled) {
    return <Navigate to="/" replace />
  }

  return <Outlet />
}

// Onboarding is OWNER-only: the create/seed/import wizard requires owner authz
// (the sample-seed endpoints are admin_or_higher, so a non-owner would hit a
// 403). Admins and staff are bounced to the dashboard.
function OwnerRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-base-200">
        <Spinner size="xl" className="text-primary" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (user?.role !== 'owner') {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-base-200">
        <Spinner size="xl" className="text-primary" />
      </div>
    )
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

function PropertyEditRedirect() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/properties/${id}`} replace />
}

// EP-141 T141-003 — /staff-calendar folded into /timesheet (the grid moved to
// components/timesheet/TimesheetWeekGrid, rendered by TimesheetPage
// alongside the hours table). `replace: true` so a bookmark/back-button never
// lands on a dead page. The public ICS endpoint
// (GET /api/staff_calendars/:token.ics) is untouched by this move.
function StaffCalendarRedirect() {
  return <Navigate to="/timesheet" replace />
}

// T149-088: /payments/new (the old invoice-attached create form) folded
// into /payments/record, which now offers the same invoice picker alongside
// the standalone/no-invoice fields. `replace: true` so a bookmark/back-button
// never lands on a dead page. The two known internal callers (bank
// reconciliation's deep-link, the AI coach's feature route) were updated to
// link straight to /payments/record; this redirect is the fallback for an
// old bookmark or external link, and does not forward query params (neither
// known caller relies on this redirect path to carry them).
function PaymentsNewRedirect() {
  return <Navigate to="/payments/record" replace />
}

function App() {
  const featureFlags = useFeatureFlags()
  const open = useSearchStore((s) => s.open)
  const navigate = useNavigate()

  // EP-77: route notification taps to the deep-linked screen (native-guarded no-op on web).
  useEffect(() => {
    void setupPushDeepLinks((path) => navigate(path))
  }, [navigate])

  // EP-80: Android hardware/gesture back button → navigate back within the app
  // instead of the default (which backgrounds/exits the app). At the history
  // root, minimize (home-like) rather than hard-exit. Native shell only.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return
    let remove: (() => void) | undefined
    void CapacitorApp.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back()
      } else {
        void CapacitorApp.minimizeApp()
      }
    }).then((handle) => {
      remove = () => void handle.remove()
    })
    return () => remove?.()
  }, [])

  // $pageview is captured automatically by posthog-js (capture_pageview: true,
  // incl. SPA history changes) — no manual per-route firing needed.

  // Global Cmd+K / Ctrl+K shortcut to open command palette
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        const tag = (e.target as HTMLElement)?.tagName
        if (tag === 'INPUT' || tag === 'TEXTAREA') return
        e.preventDefault()
        open()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open])

  return (
    <>
      {/* WEB-M6: CommandPalette is mounted ONCE in MainLayout (not here) —
          both subscribed to the same store and rendered twice at z-[100]. */}
      {/* WEB-H7: route-level fallback for the lazy chunks above — keeps lazy
          loads from bubbling to the app-shell Suspense in main.tsx. */}
      <Suspense
        fallback={
          <div className="min-h-screen flex items-center justify-center bg-base-200">
            <Spinner size="xl" className="text-primary" />
          </div>
        }
      >
      <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute>
            <SignupPage />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={<ResetPasswordPage />}
      />
      {/* EP-108: internal design-system reference — gated behind auth (any
          role) so it's not publicly reachable, but bare (no ProtectedLayout)
          since the page renders its own full-page chrome/header. */}
      <Route
        path="/design-system"
        element={
          <ProtectedRoute>
            <DesignSystemPage />
          </ProtectedRoute>
        }
      />
      <Route path="/sign/:token" element={<SignPage />} />

      {/* EP-133 T133-020 — public visitor-booking iframe (unauthenticated,
          public-key-scoped). Bare route so an authenticated operator can also
          open the iframe URL while inspecting a widget. No collision with
          any protected route — `/visit/*` is a fresh namespace. */}
      <Route path="/visit/:publicKey" element={<VisitorBookingPage />} />

      {/* Public self-service parking kiosk — unauthenticated, apikey/token-scoped.
          Bare routes (NOT wrapped in PublicRoute) so an authenticated operator can
          also open them. /parking/pay MUST precede /parking/:zoneId so the literal
          "pay" segment isn't captured as a zoneId. No collision with the protected
          exact /parking index or /properties/:id/parking/*. */}
      <Route path="/parking/pay" element={<ParkingPayPage />} />
      <Route path="/parking/:zoneId" element={<ParkingCheckInPage />} />

      {/* Onboarding — auth-required, no sidebar */}
      {/* /onboarding/explore + /onboarding/import* must be declared before the
          /onboarding catch-all (EP-86: the import flow was previously unrouted —
          the landing chooser's "Upload data" + all of the tabbed-review Uploader
          depend on these). */}
      <Route
        path="/onboarding/explore"
        element={
          <OwnerRoute>
            <ExplorerModePage />
          </OwnerRoute>
        }
      />
      {/* Import flow — auth-required, framed in OnboardingLayout (wordmark +
          Exit) via a layout route so the frame stays mounted across the
          upload → processing/review step navigation (no remount flicker). */}
      <Route
        element={
          <OwnerRoute>
            <OnboardingImportLayout />
          </OwnerRoute>
        }
      >
        <Route path="/onboarding/import" element={<ImportWizardPage />} />
        <Route path="/onboarding/import/:id" element={<ImportDetailPage />} />
      </Route>
      {/* EP-110 in-app re-entry to the same importer. It used to render inside
          ProtectedLayout, i.e. the app shell with the sidebar, and that stopped
          working once the import pages took on onboarding chrome: the full-bleed
          step bar sat inside the content column and the fixed footer ran under
          the sidebar. The owner's call (2026-08-05) is that this route uses the
          FULL onboarding import UI, so it moves to the same layout.

          It gets its OWN group rather than joining the one above because the two
          entry points do not share a guard: /onboarding/import is OwnerRoute
          (owner only), while this one has always been ProtectedRoute (any
          authenticated role). Folding them together would silently lock admins
          and staff out of an importer they can use today. Same chrome, unchanged
          access.

          No ordering concern against /properties/:id: React Router ranks a
          static segment above a dynamic one regardless of declaration order, so
          "import" is never matched as an :id. */}
      <Route
        element={
          <ProtectedRoute>
            <OnboardingImportLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/properties/import" element={<ImportWizardPage />} />
        <Route path="/properties/import/:id" element={<ImportDetailPage />} />
      </Route>
      <Route
        path="/onboarding"
        element={
          <OwnerRoute>
            <OnboardingWizardPage />
          </OwnerRoute>
        }
      />

      {/* Protected routes — ProtectedLayout stays mounted across navigation */}
      <Route element={<ProtectedRoute><ProtectedLayout /></ProtectedRoute>}>
        <Route path="/" element={<DashboardPage />} />

        {/* EP-150 (App Review 5.1.1v): account/login deletion. Every role
            (incl. accountant and staff) must reach this, deliberately
            outside AccountantBlockedRoute/MoneyRoute/AdminRoute/ModuleRoute. */}
        <Route path="/account/delete" element={<AccountDeletePage />} />

        {/* EP-159 T159-C03a: the owner opens the export link from the mail; only the owner gets the files (the API checks) */}
        <Route path="/account-export/:token" element={<AccountExportPage />} />

        {/* EP-133 §3c (T133-021) — staff front-desk arrivals view. Blocked for
            the accountant role (Owner directive: 403 on all visitor surfaces).
            D9 (EP-147) wraps this in the visitorManagement ModuleRoute so a
            typed URL on a disabled account redirects to the dashboard instead
            of reaching the page and rendering the BE's 403 as a generic
            error; the BE still enforces via VisitorManagementModuleGate. */}
        <Route element={<AccountantBlockedRoute />}>
          <Route element={<ModuleRoute module="visitorManagement" />}>
            <Route path="/visitor-arrivals" element={<VisitorArrivalsPage />} />
          </Route>
        </Route>

        {/* EP-141 T141-003 — /staff-calendar redirects into /timesheet, which
            is NOT accountant-blocked (T141-004: read is open to every role).
            The old <AccountantBlockedRoute> wrapper is removed on purpose —
            keeping it would 403 an accountant hitting an old bookmark instead
            of redirecting them, for a route that no longer renders anything
            of its own. */}
        <Route path="/staff-calendar" element={<StaffCalendarRedirect />} />

        {/* T129-009: Routes blocked for the accountant role — all out-of-scope pages
            redirect to / via AccountantBlockedRoute. In-scope pages (invoices, payments,
            expenses, bank-reconciliation, accounting, reports, vendors, timesheet) are NOT
            wrapped. Customers and Leases moved into the blocked group under D10 (EP-147,
            2026-09-13). See the comments at those route blocks below. */}

        {/* Properties — blocked for accountant (manage actions) */}
        <Route element={<AccountantBlockedRoute />}>
          <Route path="/properties" element={<PropertyListPage />} />
          <Route path="/properties/new" element={<PropertyFormPage />} />
          <Route path="/properties/:id" element={<PropertyDetailPage />} />
          <Route path="/properties/:id/edit" element={<PropertyEditRedirect />} />
        </Route>

        {/* Room Types — blocked for accountant */}
        <Route element={<AccountantBlockedRoute />}>
          <Route path="/room-types/:id" element={<RoomTypeDetailPage />} />
          <Route path="/room-types/:id/edit" element={<RoomTypeFormPage />} />
        </Route>

        {/* Units — blocked for accountant */}
        <Route element={<AccountantBlockedRoute />}>
          <Route path="/units/:id" element={<UnitDetailPage />} />
          <Route path="/units/:id/edit" element={<UnitFormPage />} />
          <Route path="/room-types/:roomTypeId/units/new" element={<UnitFormPage />} />
        </Route>

        {/* Customers: fully blocked for accountant (D10, EP-147, 2026-09-13):
            the role never reaches this page by URL, list and detail included.
            The underlying read API (CustomerPolicy#index?/show?) stays open
            regardless, because the New Invoice / Record Payment forms fill their
            customer picker from it, and closing it would break those money
            surfaces the role exists for. */}
        <Route element={<AccountantBlockedRoute />}>
          <Route path="/customers" element={<CustomerListPage />} />
          <Route path="/customers/:id" element={<CustomerDetailPage />} />
          <Route path="/customers/new" element={<CustomerFormPage />} />
          <Route path="/customers/:id/edit" element={<CustomerFormPage />} />
        </Route>

        {/* Co-owners: blocked for accountant (not in D-5 money set). D9
            (EP-147) adds the condominium ModuleRoute so a typed URL on a
            non-condominium account redirects to the dashboard instead of
            rendering the BE's "Module not enabled" 403 as a generic error. */}
        <Route element={<AccountantBlockedRoute />}>
          <Route element={<ModuleRoute module="condominium" />}>
            <Route path="/co-owners" element={<SharedOwnerListPage />} />
            {/* EP-135 Tier 0: contact directory (standalone page, same gate) */}
            <Route path="/co-owners/directory" element={<SharedOwnerDirectoryPage />} />
          </Route>
        </Route>

        {/* EP-135 Tier 1 — Governance (meetings + resolutions + committee).
            Accountant: 403 by owner directive (governance surface, not money).
            Route-level gate mirrors the existing CAF pattern — route is
            registered, the BE before_action + the condominium ModuleRoute
            (D9, EP-147) enforce access. */}
        <Route element={<AccountantBlockedRoute />}>
          <Route element={<ModuleRoute module="condominium" />}>
            <Route path="/meetings" element={<MeetingListPage />} />
            <Route path="/meetings/:id" element={<MeetingDetailPage />} />
            <Route path="/meetings/:id/audit_log" element={<AuditLogPage />} />
            <Route path="/committee" element={<CommitteePage />} />
          </Route>
        </Route>

        {/* Leases: fully blocked for accountant (D10, EP-147, 2026-09-13): the
            role never reaches this page by URL, list and detail included.
            The underlying read API (LeasePolicy#index?/show?) stays open
            regardless, because the New Invoice / Record Payment forms fill their
            lease picker from it, and closing it would break those money
            surfaces the role exists for. */}
        <Route element={<AccountantBlockedRoute />}>
          <Route path="/leases" element={<LeaseListPage />} />
          <Route path="/leases/:id" element={<LeaseDetailPage />} />
          <Route path="/leases/new" element={<LeaseFormPage />} />
          <Route path="/leases/:id/edit" element={<LeaseFormPage />} />
        </Route>

        {/* Invoices — in money set, accessible to accountant */}
        <Route path="/invoices" element={<InvoiceListPage />} />
        <Route path="/invoices/new" element={<InvoiceFormPage />} />
        <Route path="/invoices/generate" element={<InvoiceGeneratePage />} />
        <Route path="/invoices/:id" element={<InvoiceDetailPage />} />
        <Route path="/invoices/:id/edit" element={<InvoiceFormPage />} />

        {/* Payments — in money set, accessible to accountant */}
        <Route path="/payments" element={<PaymentListPage />} />
        <Route path="/payments/record" element={<QuickPayFormPage />} />
        <Route path="/payments/new" element={<PaymentsNewRedirect />} />
        <Route path="/payments/:id" element={<PaymentDetailPage />} />
        <Route path="/payments/:id/edit" element={<PaymentFormPage />} />

        {/* Common Area Fees (EP-31): blocked for accountant. The
            featureFlags.sharedOwners conditional is the separate ops
            kill-switch (Layer 4) and stays as-is; the commonAreaFees
            ModuleRoute (D9, EP-147) adds the settings-based gate
            (condominium_enabled + >=1 condominium property) alongside it. */}
        {featureFlags.sharedOwners && (
          <>
            <Route element={<AccountantBlockedRoute />}>
              <Route element={<ModuleRoute module="commonAreaFees" />}>
                <Route path="/properties/:propertyId/common-area-fees/new" element={<CommonAreaFeeFormPage />} />
                <Route path="/properties/:propertyId/common-area-fees/:id/edit" element={<CommonAreaFeeFormPage />} />
              </Route>
            </Route>
          </>
        )}
        {/* CAF sidebar page (EP-111 T111-8) — kept UNGATED by featureFlags.sharedOwners
            so it never diverges from the Sidebar's own gate (condominium_enabled +
            >=1 condominium property, no Unleash flag per the task spec); gating the
            route on the flag too could show the nav item while 404-ing the route.
            D9 (EP-147) closes the gap this comment used to describe as open: the
            route had NO settings gate at all until the commonAreaFees ModuleRoute
            below, which uses the exact same predicate as the sidebar. */}
        <Route element={<AccountantBlockedRoute />}>
          <Route element={<ModuleRoute module="commonAreaFees" />}>
            <Route path="/common-area-fees" element={<CommonAreaFeeRatesPage />} />
          </Route>
        </Route>

        {/* Meters & Readings — blocked for accountant */}
        <Route element={<AccountantBlockedRoute />}>
          <Route path="/meter-readings" element={<BulkReadingsPage />} />
          {/* Legacy /meters routes — keep aliases so old bookmarks still work */}
          <Route path="/meters" element={<Navigate to="/meter-readings" replace />} />
          <Route path="/meters/:id" element={<MeterReadingsPage />} />
          <Route path="/properties/:propertyId/readings" element={<BulkReadingsPage />} />
          {/* Legacy /readings/edit — record + edit unified into a single page */}
          <Route path="/properties/:propertyId/readings/edit" element={<BulkReadingsPage />} />
        </Route>

        {/* Parking — blocked for accountant */}
        <Route element={<AccountantBlockedRoute />}>
          <Route path="/parking" element={<ParkingIndexPage />} />
          <Route path="/properties/:propertyId/parking/zones" element={<ParkingZoneListPage />} />
          <Route path="/properties/:propertyId/parking/zones/new" element={<ParkingZoneFormPage />} />
          <Route path="/properties/:propertyId/parking/zones/:id/edit" element={<ParkingZoneFormPage />} />
          <Route path="/properties/:propertyId/parking/vehicles" element={<VehicleListPage />} />
          <Route path="/properties/:propertyId/parking/vehicles/new" element={<VehicleFormPage />} />
          <Route path="/properties/:propertyId/parking/vehicles/:id/edit" element={<VehicleFormPage />} />
          <Route path="/properties/:propertyId/parking/permits" element={<ParkingPermitListPage />} />
          <Route path="/properties/:propertyId/parking/permits/new" element={<ParkingPermitFormPage />} />
          <Route path="/properties/:propertyId/parking/permits/:id/edit" element={<ParkingPermitFormPage />} />
          <Route path="/properties/:propertyId/parking/daily" element={<DailyParkingLogPage />} />
          {/* B-048: LPR is single-gated on the account setting (lpr_enabled), like the
              other admin-managed modules — the route stays registered (as accounting's
              do) and the BE before_action enforces access. D9 (EP-147) adds the lpr
              ModuleRoute so a typed URL on a disabled account redirects to the
              dashboard instead of reaching the page; this also replaces the page's
              own in-page Navigate guard (LprEventLogPage no longer self-guards). */}
          <Route element={<ModuleRoute module="lpr" />}>
            <Route path="/properties/:propertyId/parking/lpr" element={<LprEventLogPage />} />
          </Route>
          {/* EP-161: ที่จอดรถวันนี้, the kiosk sessions of the parking fee module. */}
          <Route element={<ModuleRoute module="parkingFee" />}>
            <Route path="/properties/:propertyId/parking/sessions" element={<ParkingSessionsPage />} />
          </Route>
        </Route>

        {/* Visitor management (EP-133) — non-money, blocked for accountant per
            §9a. T133-025 IS built (VisitorManagementModuleGate, 2026-08-21);
            this comment previously said "not yet built" and was never revisited,
            which is how the surface shipped ungated on the client while every
            request behind it returned 403. D9 (EP-147) adds the visitorManagement
            ModuleRoute so a typed URL redirects to the dashboard; the BE still
            enforces via VisitorManagementModuleGate. */}
        <Route element={<AccountantBlockedRoute />}>
          <Route element={<ModuleRoute module="visitorManagement" />}>
            <Route path="/properties/:propertyId/visitor-widget" element={<VisitorWidgetSettingsPage />} />
          </Route>
        </Route>

        {/* Maintenance — blocked for accountant */}
        <Route element={<AccountantBlockedRoute />}>
          <Route path="/maintenances" element={<MaintenanceListPage />} />
          <Route path="/maintenances/new" element={<MaintenanceFormPage />} />
          <Route path="/maintenances/:id" element={<MaintenanceDetailPage />} />
          <Route path="/maintenances/:id/edit" element={<MaintenanceFormPage />} />
          {/* Legacy /maintenance routes — keep aliases so old bookmarks still work */}
          <Route path="/maintenance" element={<Navigate to="/maintenances" replace />} />
          <Route path="/maintenance/*" element={<Navigate to="/maintenances" replace />} />
        </Route>

        {/* Parcels — blocked for accountant */}
        <Route element={<AccountantBlockedRoute />}>
          <Route path="/parcels" element={<ParcelListPage />} />
          <Route path="/parcels/new" element={<ParcelFormPage />} />
          {/* scan must come before :id so /parcels/scan does not match the detail route */}
          <Route path="/parcels/scan" element={<ScanParcelPage />} />
          <Route path="/parcels/:id" element={<ParcelDetailPage />} />
          <Route path="/parcels/:id/edit" element={<ParcelFormPage />} />
        </Route>

        {/* Expenses — in money set, accessible to accountant */}
        <Route path="/expenses" element={<ExpenseListPage />} />
        <Route path="/expenses/new" element={<ExpenseFormPage />} />
        <Route path="/expenses/:id" element={<ExpenseDetailPage />} />
        <Route path="/expenses/:id/edit" element={<ExpenseFormPage />} />

        {/* Timesheet — accessible to accountant (own-entry per D-5/Q-4) */}
        <Route path="/timesheet" element={<TimesheetPage />} />

        {/* Announcements (EP-26, feature-flagged): blocked for accountant.
            D9 (EP-147) adds the announcements ModuleRoute so a typed URL on
            an account with the setting off redirects to the dashboard. */}
        {featureFlags.announcements && (
          <Route element={<AccountantBlockedRoute />}>
            <Route element={<ModuleRoute module="announcements" />}>
              <Route path="/announcements" element={<AnnouncementListPage />} />
              <Route path="/announcements/new" element={<AnnouncementFormPage />} />
              <Route path="/announcements/:id" element={<AnnouncementDetailPage />} />
              <Route path="/announcements/:id/edit" element={<AnnouncementFormPage />} />
            </Route>
          </Route>
        )}

        {/* Incentives (Campaigns, Catalog, Promotions, EP-74): blocked for
            accountant. D9 (EP-147) adds two ModuleRoutes: campaigns/catalog
            gate on incentivesRewards, promotions gates on incentivesAny
            (rewards OR retention), mirroring the Sidebar's own split, which
            was the fix for a 2026-08-05 regression where Promotions leaked
            under the rewards-only module. */}
        {(featureFlags.loyaltyPoints || featureFlags.renewalBenefits) && (
          // Owner and admin only (every incentives policy is admin_or_higher):
          // staff got a 403 and the error page here. The sidebar hides the
          // three entries on the same rule.
          <Route element={<AdminRoute><Outlet /></AdminRoute>}>
            {featureFlags.loyaltyPoints && (
              <Route element={<ModuleRoute module="incentivesRewards" />}>
                <Route path="/incentives/campaigns" element={<CampaignsListPage />} />
                {/* No dedicated `/new` route — it would match without an `:id`
                    param, so CampaignDetailPage's `isNew = id === 'new'` stayed
                    false (new campaigns rendered as existing). `/:id` matches
                    `new` and yields id="new". */}
                <Route path="/incentives/campaigns/:id" element={<CampaignDetailPage />} />
                <Route path="/incentives/catalog" element={<CatalogListPage />} />
              </Route>
            )}
            <Route element={<ModuleRoute module="incentivesAny" />}>
              <Route path="/incentives/promotions" element={<PromotionsPage />} />
            </Route>
          </Route>
        )}

        {/* CMMS: Equipment & Work Orders (EP-34). D9 (EP-147) adds the cmms
            ModuleRoute so a typed URL redirects to the dashboard when the
            module is off. /vendors is DELIBERATELY EXCLUDED from this wrap:
            it's in the separate MoneyRoute block below and stays reachable
            by admin/owner AND accountant regardless of the CMMS flag/setting
            (CMMS has no server-side module gate at all, EP-113 §7.4). */}
        {featureFlags.cmms && (
          <Route element={<ModuleRoute module="cmms" />}>
            <Route path="/equipment" element={<EquipmentListPage />} />
            <Route path="/equipment/new" element={<EquipmentFormPage />} />
            <Route path="/equipment/:id" element={<EquipmentDetailPage />} />
            <Route path="/equipment/:id/edit" element={<EquipmentFormPage />} />
            <Route path="/work-orders" element={<WorkOrderListPage />} />
            <Route path="/work-orders/new" element={<WorkOrderFormPage />} />
            <Route path="/work-orders/:id" element={<WorkOrderDetailPage />} />
            <Route path="/work-orders/:id/edit" element={<WorkOrderFormPage />} />
          </Route>
        )}

        {/* Reports. These carry revenue/expense figures, so they sit behind
            MoneyRoute like every other report — previously they were bare
            <Route>s, which let staff reach P&L and expense totals by URL
            even though /reports itself is money-gated. */}
        <Route element={<MoneyRoute />}>
          <Route path="/reports/expenses/summary" element={<ExpenseSummaryReportPage />} />
          <Route path="/reports/expenses/trend" element={<ExpenseTrendReportPage />} />
          <Route path="/reports/profit-loss" element={<ProfitLossReportPage />} />
        </Route>

        {/* T129-009: Money-set routes accessible to admin/owner AND accountant.
            These were formerly in the AdminRoute block (staff-blocked); now moved
            here so accountant can reach them. AdminRoute now only holds truly
            admin-only pages (settings, team, templates, parking-fees). */}

        {/* Vendors — in money set for accountant (D-5) */}
        <Route element={<MoneyRoute />}>
          <Route path="/vendors" element={<VendorListPage />} />
          <Route path="/vendors/new" element={<VendorFormPage />} />
          <Route path="/vendors/:id" element={<VendorDetailPage />} />
          <Route path="/vendors/:id/edit" element={<VendorFormPage />} />
        </Route>

        {/* Reports — accessible to admin/owner AND accountant */}
        <Route element={<MoneyRoute />}>
          <Route path="/reports" element={<ReportsIndexPage />} />

          {/* Collections group (RPT-5) */}
          <Route path="/reports/collections" element={<CollectionsGroupPage />} />
          <Route path="/reports/collections/stats" element={<CollectionStatsReportPage />} />
          <Route path="/reports/collections/stats/details" element={<CollectionStatsDetailsPage />} />
          <Route path="/reports/collections/scorecard" element={<TenantScorecardReportPage />} />
          <Route
            path="/reports/collections/scorecard/tenant/:id"
            element={<TenantScorecardDetailPage />}
          />
          <Route path="/reports/collections/overdue" element={<OverdueInvoicesReportPage />} />

          {/* Occupancy group (RPT-6) — landing replaces the pre-redesign single page */}
          <Route path="/reports/occupancy" element={<OccupancyGroupPage />} />
          <Route path="/reports/occupancy/stats" element={<OccupancyStatsReportPage />} />
          <Route path="/reports/occupancy/stats/details" element={<OccupancyStatsDetailsPage />} />
          <Route path="/reports/occupancy/vacant" element={<VacantUnitsReportPage />} />
          <Route path="/reports/revenue" element={<RevenueReportPage />} />

          {/* Profitability group (RPT-7) */}
          <Route path="/reports/profitability/pl" element={<ProfitLossGlancePage />} />
          <Route path="/reports/profitability/pl/details" element={<ProfitLossDetailsPage />} />

          {/* Records group (RPT-8) — CAF card is condo-gated in RecordsGroupPage */}
          <Route path="/reports/records" element={<RecordsGroupPage />} />
          <Route path="/reports/records/timesheets" element={<TimesheetsReportPage />} />
          <Route path="/reports/records/maintenance" element={<MaintenanceReportPage />} />
          <Route path="/reports/records/caf" element={<CafCollectionReportPage />} />
          <Route
            path="/reports/records/bank-reconciliation"
            element={<BankReconciliationReportPage />}
          />
          <Route
            path="/reports/records/financial-statements"
            element={<FinancialStatementsReportPage />}
          />

          {/* Parking group (RPT-9) — ParkingGroupPage self-guards on has_parking_zones */}
          <Route path="/reports/parking" element={<ParkingGroupPage />} />
          <Route path="/reports/parking/occupancy" element={<ParkingOccupancyReportPage />} />
          <Route path="/reports/parking/revenue" element={<ParkingRevenueReportPage />} />
          <Route path="/reports/parking/registry" element={<ParkingRegistryReportPage />} />
        </Route>

        {/* Bank Reconciliation — in money set for accountant (D-5) */}
        <Route element={<MoneyRoute />}>
          <Route path="/bank-reconciliation" element={<BankReconciliationPage />} />
        </Route>

        {/* Closing Package (T129-012) — in money set for accountant (D-5) */}
        <Route element={<MoneyRoute />}>
          <Route path="/closing-package" element={<ClosingPackagePage />} />
        </Route>

        {/* Accounting / GL (EP-35): in money set for accountant (D-5). D9
            (EP-147) adds the accounting ModuleRoute, which was previously MoneyRoute
            only, with no settings gate at all. */}
        <Route element={<MoneyRoute />}>
          <Route element={<ModuleRoute module="accounting" />}>
            <Route path="/accounting/chart-of-accounts" element={<ChartOfAccountsPage />} />
            <Route path="/accounting/journal-entries" element={<JournalEntryListPage />} />
            <Route path="/accounting/journal-entries/new" element={<JournalEntryFormPage />} />
            <Route path="/accounting/journal-entries/:id" element={<JournalEntryDetailPage />} />
            <Route path="/accounting/journal-entries/:id/edit" element={<JournalEntryFormPage />} />
            <Route path="/accounting/financial-statements" element={<FinancialStatementsPage />} />
            <Route path="/accounting/fixed-assets" element={<FixedAssetListPage />} />
            <Route path="/accounting/fixed-assets/new" element={<FixedAssetFormPage />} />
            <Route path="/accounting/fixed-assets/:id" element={<FixedAssetDetailPage />} />
            <Route path="/accounting/fixed-assets/:id/edit" element={<FixedAssetFormPage />} />
          </Route>
        </Route>

        {/* Debt Collection (EP-36): admin-only (not in accountant money set).
            D9 (EP-147) adds the debtCollection ModuleRoute so a typed URL on
            an account with the setting off redirects to the dashboard. */}
        {featureFlags.debtCollection && (
          <Route element={<AccountantBlockedRoute />}>
            <Route element={<ModuleRoute module="debtCollection" />}>
              <Route path="/debt-collection" element={<DebtCaseListPage />} />
              <Route path="/debt-collection/:id" element={<DebtCaseDetailPage />} />
            </Route>
          </Route>
        )}
      </Route>

      {/* Admin-only routes — same layout, different guard (staff+accountant blocked) */}
      <Route element={<AdminRoute><ProtectedLayout /></AdminRoute>}>
        {/* D9 (EP-147) adds the parkingFee ModuleRoute, which was previously reachable
            by any admin/owner regardless of the parking_fee_enabled setting,
            which also replaces the page's own in-page Navigate guard
            (ParkingFeeConfigPage no longer self-guards). */}
        <Route element={<ModuleRoute module="parkingFee" />}>
          <Route path="/properties/:propertyId/parking-fees" element={<ParkingFeeConfigPage />} />
        </Route>
        <Route path="/settings/templates" element={<TemplateListPage />} />
        <Route path="/settings/templates/new" element={<TemplateFormPage />} />
        <Route path="/settings/templates/:id" element={<TemplateDetailPage />} />
        <Route path="/settings/templates/:id/edit" element={<TemplateFormPage />} />
        <Route path="/settings/activity-log" element={<ActivityLogPage />} />
        {/* EP-144: the platform-invoice detail. AdminRoute already blocks
            staff and accountant; the page itself is owner-only (Q-9) and
            redirects an admin to /settings, mirroring the tab's deep-link
            fallback in SettingsPage. */}
        <Route
          path="/settings/subscription/invoices/:id"
          element={<SubscriptionInvoiceDetailPage />}
        />
        <Route path="/settings" element={<SettingsPage />} />
        {/* EP-160 S2: the support access mail and push link here; the page
            opens its FlowRent help tab (the request id rides in ?request=). */}
        <Route path="/settings/support-access" element={<SettingsPage />} />
        <Route path="/settings/team" element={<TeamListPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
      </Suspense>
    </>
  )
}

export default App
